import React, { useCallback, useContext, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { LoadResponse } from "../../../../models/api/load";
import { EmployeeGroupDTO } from "../../../../models/employee-groups";
import { TnACounterDTO } from "../../../../models/tna";
import { exportTnAToExcel } from "../../../../utils/export-presence-and-leaves";
import { getNormaleDateOrMonth, intlFormatDateShort, intlFormatDateWithHour } from "../../../../utils/time-utils";
import { monthsValues } from "../../../CustomDropdowns/CustomDropdownMonthsValues/CustomDropdownMonthsValues";
import ExportToExcelDialog from "../../ExportToExcelDialog/ExportToExcelDialog";
import { MyTnAContext } from "../../MyTeamPresenceAndLeaves";
import { excelStyles } from "../../utils";

interface Props {
  disabled: boolean;
  loadEmployeeGroup: () => Promise<EmployeeGroupDTO | undefined>;
  loadTnAVacationTrackingData: (pageSize?: number) => Promise<LoadResponse<TnACounterDTO> | undefined>;
  allItems: () => TnACounterDTO[];
}

const ExportVacationTrackingToExcel = ({
  disabled,
  loadEmployeeGroup,
  loadTnAVacationTrackingData,
  allItems,
}: Props) => {
  const { t } = useTranslation();
  const tRP = "components.team_presence_and_leaves_vacation_tracker";
  const translationFields = `${tRP}.fields`;
  const tRPCommon = "pages.company_account_profile.tables.employees.fields";

  const {
    legalEntity: { legalEntityOption, selectedLegalEntity, legalEntityQuickFilter },
    employeeGroup: { employeeGroupOption, employeeGroupQuickFilter },
    currentEmployees: { onlyCurrentEmployeesOptionValue, onlyCurrentEmployeesQuickFilter },
    searchingDate: { year, month, yearQuickFilter },
  } = useContext(MyTnAContext);

  const rowsBeforeHeaderInExcel = useCallback(
    (employeeGroup: EmployeeGroupDTO | undefined) => {
      return [
        {
          data: ["Vacation tracking"],
          fontSize: 16,
          isBold: true,
        },
        {
          data: [`Report date ${intlFormatDateWithHour(new Date())}`],
          fontSize: 14,
          isBold: false,
          emptyRowsUnder: 1,
        },
        {
          data: [
            `Legal entities: ${!legalEntityOption?.legalEntityIds?.length ? "All" : selectedLegalEntity?.name ?? ""}`,
          ],
          fontSize: 16,
          isBold: true,
          emptyRowsUnder: 1,
        },
        {
          data: [
            `Employee group: ${
              !employeeGroupOption?.employeeGroupIds?.length ? "All groups" : `${employeeGroup?.name ?? ""}`
            }`,
          ],
          fontSize: 12,
          isBold: false,
        },
        {
          data: [
            `Employee status: ${
              onlyCurrentEmployeesOptionValue?.onlyCurrentEmployees ? "Only current employees" : "All employees"
            }`,
          ],
          fontSize: 12,
          isBold: false,
          emptyRowsUnder: 1,
        },
        {
          data: [`Period: ${year} ${t(monthsValues.find((currMonth) => currMonth.value === month)?.key ?? "")}`],
          fontSize: 14,
          isBold: true,
        },
      ];
    },
    [
      legalEntityOption?.legalEntityIds,
      selectedLegalEntity?.name,
      employeeGroupOption,
      onlyCurrentEmployeesOptionValue,
      year,
      month,
    ]
  );

  const headerNames = useMemo(() => {
    return [
      `${t(`${tRPCommon}.employee_code`)}`,
      `${t(`${tRPCommon}.internal_id`)}`,
      `${t("common_words.employee")}`,
      `${t(`${tRPCommon}.birth_date`)}`,
      `${t("common_words.legal_entity")}`,
      `${t("common_words.tna_employee_group")}`,
      `${t(`${translationFields}.type`)}`,
      `${t(`${translationFields}.from_previous_year`)}`,
      `${t(`${translationFields}.already_generated_this_year`)} ${year}`,
      `${t(`${translationFields}.corrections`)} ${year}`,
      `${t(`${translationFields}.already_used_or_planned`)} ${year}`,
      `${t(`${translationFields}.total_balance`)} ${year}`,
    ];
  }, [year]);

  const dataToExportAsExcel = useCallback(
    (allItems: TnACounterDTO[]) => {
      return allItems.map((item) => {
        return {
          [`${t(`${tRPCommon}.employee_code`)}`]: { value: item.employee?.employeeCode ?? " " },
          [`${t(`${tRPCommon}.internal_id`)}`]: { value: item.employee?.internalId ?? " " },
          [`${t("common_words.employee")}`]: {
            value: item.employee?.name ?? " ",
            style: { ...excelStyles.employeeCellStyle },
          },
          [`${t(`${tRPCommon}.birth_date`)}`]: {
            value: item.employee?.birthDate ? intlFormatDateShort(item.employee?.birthDate) : " ",
          },
          [`${t("common_words.legal_entity")}`]: { value: item.employee?.legalEntity?.name ?? " " },
          [`${t("common_words.tna_employee_group")}`]: { value: item?.employee?.tnAGroup?.name ?? " " },
          [`${t(`${translationFields}.type`)}`]: { value: item?.counterType?.name ?? " " },
          [`${t(`${translationFields}.from_previous_year`)}`]: {
            value: item.fromPrevYear ? Number(item.fromPrevYear).toFixed(2) : " ",
          },
          [`${t(`${translationFields}.already_generated_this_year`)} ${year}`]: {
            value: item.generatedThisYear ? Number(item.generatedThisYear).toFixed(2) : " ",
          },
          [`${t(`${translationFields}.corrections`)} ${year}`]: {
            value: item.correctionsThisYear ? Number(item.correctionsThisYear).toFixed(2) : " ",
          },
          [`${t(`${translationFields}.already_used_or_planned`)} ${year}`]: {
            value: item.usedOrPlanned ? Number(item.usedOrPlanned).toFixed(2) : " ",
          },
          [`${t(`${translationFields}.total_balance`)} ${year}`]: {
            value: item.balance ? Number(item.balance).toFixed(2) : " ",
          },
          sortDirection: headerNames,
        };
      });
    },
    [headerNames, year]
  );

  const exportToExcel = useCallback(
    async (option: "exportAllData" | "exportCurrent" = "exportCurrent") => {
      if (option === "exportCurrent") {
        const employeeGroup = employeeGroupOption?.employeeGroupIds?.length ? await loadEmployeeGroup() : undefined;

        exportTnAToExcel(
          dataToExportAsExcel(allItems()),
          headerNames as string[],
          `${new Date().getFullYear()} ${getNormaleDateOrMonth(new Date().getMonth() + 1)} ${getNormaleDateOrMonth(
            new Date().getDate()
          )} ${!legalEntityOption?.legalEntityIds?.length ? "All legal entities" : selectedLegalEntity?.name ?? ""} ${
            !employeeGroupOption?.employeeGroupIds?.length ? "All groups" : `${employeeGroup?.name ?? ""}`
          } Vacation tracking`,
          rowsBeforeHeaderInExcel(employeeGroup)
        );
      }

      if (option === "exportAllData") {
        const employeeGroup = employeeGroupOption?.employeeGroupIds?.length ? await loadEmployeeGroup() : undefined;

        const firstResponse = await loadTnAVacationTrackingData();

        if (firstResponse?.error) return;

        const allTnAVacationTrackingData = await loadTnAVacationTrackingData(firstResponse?.totalItems);

        if (!!allTnAVacationTrackingData?.error || !allTnAVacationTrackingData?.items) return;

        exportTnAToExcel(
          dataToExportAsExcel(allTnAVacationTrackingData.items),
          headerNames as string[],
          `${new Date().getFullYear()} ${getNormaleDateOrMonth(new Date().getMonth() + 1)} ${getNormaleDateOrMonth(
            new Date().getDate()
          )} ${!legalEntityOption?.legalEntityIds?.length ? "All legal entities" : selectedLegalEntity?.name ?? ""} ${
            !employeeGroupOption?.employeeGroupIds?.length ? "All groups" : `${employeeGroup?.name ?? ""}`
          } Vacation tracking`,
          rowsBeforeHeaderInExcel(employeeGroup)
        );
      }
    },
    [
      rowsBeforeHeaderInExcel,
      loadEmployeeGroup,
      headerNames,
      dataToExportAsExcel,
      employeeGroupOption?.employeeGroupIds,
      loadTnAVacationTrackingData,
    ]
  );

  return (
    <ExportToExcelDialog
      handleExportToExcel={exportToExcel}
      disabled={disabled}
    />
  );
};

export default ExportVacationTrackingToExcel;
